import React, {useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import {useQuery} from "@apollo/client";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {LIST_USERS} from "~/src/graphql/userQueries";
import {useAIServices} from "~/src/context/AIServicesHooks";
import {useUser} from "~/src/components/user/Context/UserProvider";
import {usePermissions} from "~/src/context/AuthzProvider";
import StyledTooltip from "~/src/components/tooltips/StyledTooltip";
import {getDisplayName} from "~/src/resources/users";
import {
  getSessionShares,
  revokeSessionShare,
  shareSession,
} from "~/src/api/trustedAdvisor/sessions";
import {useRexAITrustedAdvisorStyles} from "./useRexAITrustedAdvisorStyles";
import {contains, filter, findWhere, isEmpty, map, some, sortBy} from "underscore";

const canAccessMaestro = (user) =>
  some(
    user?.authorization?.permissions,
    ({subject, action}) => subject === "TrustedAdvisor" && action === "access",
  );

const formatSharingEvent = (event, users, currentUser) => {
  const actorUser =
    event.actorUserId === currentUser?.id ? currentUser : findWhere(users, {id: event.actorUserId});
  const targetUser = findWhere(users, {id: event.targetUserId});
  const actor = actorUser?.fullName || event.actorDisplayName || event.actorEmail || "Someone";
  const target =
    targetUser?.fullName || event.targetDisplayName || event.targetEmail || "the participant";

  switch (event.eventType) {
    case "shared":
      return `${actor} shared the session with ${target}.`;
    case "readded":
      return `${actor} restored access for ${target}.`;
    case "revoked":
      return `${actor} revoked access from ${target}.`;
    case "left":
      return `${actor} left the session.`;
    case "session_deleted":
      return `${actor} deleted the session.`;
    default:
      return `${actor} updated sharing for ${target}.`;
  }
};

const formatSharingEventDate = (occurredAt) => {
  const date = new Date(occurredAt);
  if (!occurredAt || Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleString(undefined, {dateStyle: "short", timeStyle: "short"});
};

const SessionSharingDialog = ({open, session, readOnly = false, onClose, onChanged}) => {
  const {classes} = useRexAITrustedAdvisorStyles();
  const {apiClient} = useAIServices();
  const {user: currentUser} = useUser();
  const {canShareTrustedAdvisor} = usePermissions();
  const {data, loading} = useQuery(LIST_USERS, {skip: !open});
  const [sharing, setSharing] = useState();
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [error, setError] = useState("");
  const [isUpdatingSharing, setIsUpdatingSharing] = useState(false);
  const [loadingMoreHistory, setLoadingMoreHistory] = useState(false);

  const refresh = async ({syncList = false} = {}) => {
    if (!session?.sessionId) {
      return;
    }
    const nextSharing = await getSessionShares(apiClient, {sessionId: session.sessionId});
    setSharing(nextSharing);
    if (
      syncList &&
      !readOnly &&
      Boolean(session.hasActiveParticipants) !== Boolean(nextSharing.hasActiveParticipants)
    ) {
      await onChanged?.();
    }
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    setSharing(undefined);
    setSelectedUsers([]);
    setError("");
    refresh({syncList: true}).catch(() => setError("Could not load sharing details."));
  }, [open, session?.sessionId]);

  const eligibleUsers = useMemo(() => {
    const activeIds = map(sharing?.participants || [], ({targetUserId}) => targetUserId);
    return sortBy(
      filter(
        data?.users || [],
        (candidate) =>
          candidate.isEnabled &&
          !candidate.isDeleted &&
          candidate.email !== currentUser?.email &&
          candidate.email?.toLowerCase() !== "support+systemuser@breachrx.com" &&
          canAccessMaestro(candidate) &&
          !contains(activeIds, candidate.id),
      ),
      (candidate) => getDisplayName(candidate, true)?.toLocaleLowerCase() || "",
    );
  }, [currentUser?.email, data?.users, sharing?.participants]);

  const sortedParticipants = useMemo(() => {
    const resolvedParticipants = map(sharing?.participants || [], (participant) => {
      const participantUser = findWhere(data?.users || [], {id: participant.targetUserId});
      const email = participantUser?.email || participant.targetEmail || "";
      const displayName =
        getDisplayName(
          {
            fullName: participantUser?.fullName || participant.targetDisplayName,
            email,
          },
          true,
        ) || "Participant";
      return {participant, participantUser, displayName, email};
    });
    return sortBy(resolvedParticipants, ({displayName}) => displayName.toLocaleLowerCase());
  }, [data?.users, sharing?.participants]);

  const handleShare = async () => {
    if (isUpdatingSharing) {
      return;
    }
    setIsUpdatingSharing(true);
    try {
      setError("");
      const result = await shareSession(apiClient, {
        sessionId: session.sessionId,
        userIds: map(selectedUsers, ({id}) => id),
      });
      const failed = filter(result.results, ({success}) => !success);
      if (failed.length) {
        setError(`${failed.length} user(s) could not be shared.`);
      }
      setSelectedUsers([]);
      await refresh({syncList: true});
    } catch {
      setError("Could not share this session.");
    } finally {
      setIsUpdatingSharing(false);
    }
  };

  const handleRevoke = async (userId) => {
    if (isUpdatingSharing) {
      return;
    }
    setIsUpdatingSharing(true);
    try {
      setError("");
      await revokeSessionShare(apiClient, {sessionId: session.sessionId, userId});
      await refresh({syncList: true});
    } catch {
      setError("Could not remove access.");
    } finally {
      setIsUpdatingSharing(false);
    }
  };

  const handleLoadMoreHistory = async () => {
    try {
      setLoadingMoreHistory(true);
      const nextPage = await getSessionShares(apiClient, {
        sessionId: session.sessionId,
        historyCursor: sharing.historyNextCursor,
      });
      setSharing((current) => ({
        ...current,
        history: [...(current.history || []), ...(nextPage.history || [])],
        historyNextCursor: nextPage.historyNextCursor,
      }));
    } catch {
      setError("Could not load more sharing history.");
    } finally {
      setLoadingMoreHistory(false);
    }
  };

  const ownerUserId = sharing?.ownerUserId || currentUser?.id;
  const owner =
    findWhere(data?.users || [], {id: ownerUserId}) || (!readOnly ? currentUser : undefined);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{readOnly ? "People with access" : "Share session"}</DialogTitle>
      <DialogContent>
        {!sharing && !error && (
          <Box aria-label="Loading sharing details" className={classes.sharingDialogLoading}>
            <Skeleton variant="rounded" height={56} />
            <Skeleton width={130} className={classes.sharingDialogLoadingHeading} />
            {map([0, 1], (row) => (
              <Box key={row} className={classes.sharingDialogLoadingRow}>
                <Skeleton
                  variant="circular"
                  width={40}
                  height={40}
                  className={classes.sharingDialogLoadingAvatar}
                />
                <Box className={classes.sharingDialogLoadingText}>
                  <Skeleton width="45%" />
                  <Skeleton width="65%" />
                </Box>
              </Box>
            ))}
          </Box>
        )}
        {!sharing && error && <Typography color="error">{error}</Typography>}
        {sharing && (
          <>
            {!readOnly && (
              <Autocomplete
                multiple
                disabled={!canShareTrustedAdvisor || isUpdatingSharing}
                options={eligibleUsers}
                value={selectedUsers}
                loading={loading}
                getOptionLabel={(option) => getDisplayName(option, true)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(event, value) => setSelectedUsers(value)}
                getOptionDisabled={(option) =>
                  selectedUsers.length >=
                  (sharing.maxTargetsPerRequest || Number.MAX_SAFE_INTEGER) &&
                  !some(selectedUsers, ({id}) => id === option.id)}
                renderInput={(params) => <TextField {...params} label="People" margin="normal" />}
              />
            )}
            {error && <Typography color="error">{error}</Typography>}
            <Typography variant="subtitle2" className={classes.sharingDialogSection}>
              People with access
            </Typography>
            <List dense>
              <ListItem key="owner">
                <ListItemAvatar>
                  <Avatar src={owner?.profileDocumentPreview}>
                    {(owner?.fullName || owner?.email || "?")[0]}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={owner?.fullName || owner?.email || "Session owner"}
                  secondary="Owner"
                />
              </ListItem>
              {map(sortedParticipants, ({participant, participantUser, displayName, email}) => {
                const unavailable =
                  !participantUser || !participantUser.isEnabled || participantUser.isDeleted;
                return (
                  <ListItem
                    key={participant.targetUserId}
                    secondaryAction={
                      !readOnly && (
                        <StyledTooltip title="Remove access" placement="top">
                          <IconButton
                            edge="end"
                            aria-label="Remove access"
                            disabled={isUpdatingSharing}
                            onClick={() => handleRevoke(participant.targetUserId)}
                          >
                            <CloseIcon />
                          </IconButton>
                        </StyledTooltip>
                      )
                    }
                  >
                    <ListItemAvatar>
                      <Avatar src={participantUser?.profileDocumentPreview}>
                        {displayName[0]}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={displayName}
                      secondary={`${email}${unavailable ? " • Unavailable" : ""}`}
                    />
                  </ListItem>
                );
              })}
            </List>
            {!isEmpty(sharing.history) && (
              <Box className={classes.sharingDialogSection}>
                <Divider className={classes.sharingDialogHistoryDivider} />
                <Typography variant="subtitle2">Sharing history</Typography>
                <Box className={classes.sharingDialogHistory}>
                  {map(sharing.history, (event) => {
                    const occurredAt = formatSharingEventDate(event.occurredAt);
                    return (
                      <Typography key={event.eventId} variant="caption" display="block">
                        {occurredAt ? `${occurredAt}: ` : ""}
                        {formatSharingEvent(event, data?.users || [], currentUser)}
                      </Typography>
                    );
                  })}
                  {sharing.historyNextCursor && (
                    <Button
                      size="small"
                      disabled={loadingMoreHistory}
                      onClick={handleLoadMoreHistory}
                    >
                      {loadingMoreHistory ? "Loading…" : "Load more"}
                    </Button>
                  )}
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Done</Button>
        {!readOnly && (
          <Button
            variant="contained"
            disabled={
              !sharing || !canShareTrustedAdvisor || !selectedUsers.length || isUpdatingSharing
            }
            onClick={handleShare}
          >
            Share
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

SessionSharingDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  session: PropTypes.object.isRequired,
  readOnly: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onChanged: PropTypes.func,
};

export default SessionSharingDialog;
